/**
 * Lead Discovery Dashboard
 * Premium enterprise lead search and discovery interface
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Filter,
  Star,
  Mail,
  Phone,
  Globe,
  MapPin,
  TrendingUp,
  Target,
  Database,
  Sparkles,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadDiscoveryDashboard = ({ user, onNavigate }) => {
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    category: '',
    score_label: ''
  });
  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(true);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchMetrics = useCallback(async () => {
    try {
      // Fetch Phase 9A-9C metrics
      const response = await fetch(`${API}/phase9/leads/metrics`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        
        // Fetch Phase 9D sales engine metrics
        const phase9dResponse = await fetch(`${API}/phase9/dashboard/metrics`, {
          headers: authHeaders()
        });
        if (phase9dResponse.ok) {
          const phase9dData = await phase9dResponse.json();
          // Merge metrics
          setMetrics({ ...data, ...phase9dData });
        } else {
          setMetrics(data);
        }
      }
    } catch (error) {
      console.error('Metrics error:', error);
    } finally {
      setMetricsLoading(false);
    }
  }, [authHeaders]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('keyword', searchQuery);
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.category) params.append('category', filters.category);
      if (filters.score_label) params.append('score_label', filters.score_label);
      params.append('limit', '50');

      const response = await fetch(`${API}/phase9/leads?${params}`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      } else {
        toast.error('Failed to fetch leads');
      }
    } catch (error) {
      toast.error('Error fetching leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, searchQuery, filters]);

  useEffect(() => {
    fetchMetrics();
    fetchLeads();
  }, [fetchMetrics, fetchLeads]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      fetchLeads();
    }
  };

  const getScoreBadgeClass = (label) => {
    switch (label) {
      case 'Hot': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'Warm': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'Cold': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'Bad Fit': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  const LeadCard = ({ lead }) => (
    <article
      className="glass-panel cursor-pointer hover:border-[var(--line-gold)] transition-all duration-300"
      onClick={() => onNavigate('detail', lead)}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="text-base font-semibold text-white/90 mb-1">{lead.clinic_name}</h3>
          <div className="flex items-center gap-1 text-xs text-white/60">
            <MapPin className="h-3 w-3" />
            {lead.city}, {lead.state}
          </div>
        </div>
        {lead.ai_lead_score !== null && lead.ai_lead_score !== undefined && (
          <Badge className={`${getScoreBadgeClass(lead.ai_lead_score_label)} text-xs font-mono`}>
            {lead.ai_lead_score} — {lead.ai_lead_score_label}
          </Badge>
        )}
      </div>

      <div className="space-y-2">
        {lead.email && (
          <div className="flex items-center gap-2 text-xs text-white/70">
            <Mail className="h-3.5 w-3.5" />
            <span className="truncate">{lead.email}</span>
          </div>
        )}
        {lead.phone && (
          <div className="flex items-center gap-2 text-xs text-white/70">
            <Phone className="h-3.5 w-3.5" />
            {lead.phone}
          </div>
        )}
        {lead.website && (
          <div className="flex items-center gap-2 text-xs text-white/70">
            <Globe className="h-3.5 w-3.5" />
            <span className="truncate">{lead.website}</span>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Badge variant="outline" className="text-[10px] border-white/10 text-white/60">
            {lead.category || 'Med Spa'}
          </Badge>
          {lead.rating && (
            <Badge variant="outline" className="text-[10px] border-white/10 text-white/60 flex items-center gap-1">
              <Star className="h-3 w-3 fill-[var(--gold)] text-[var(--gold)]" />
              {lead.rating} ({lead.review_count ?? lead.reviews_count ?? 0})
            </Badge>
          )}
        </div>
      </div>
    </article>
  );

  const MetricCard = ({ label, value, icon: Icon, loading }) => (
    <article className="glass-panel">
      {loading ? (
        <>
          <Skeleton className="h-4 w-20 mb-2 bg-white/10" />
          <Skeleton className="h-8 w-32 bg-white/10" />
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Icon className="h-4 w-4 text-[var(--gold)]" />
            <p className="text-[11px] uppercase tracking-[0.08em] text-white/60">{label}</p>
          </div>
          <strong className="text-2xl font-semibold text-white">{value}</strong>
        </>
      )}
    </article>
  );

  return (
    <div className="page-stack" data-testid="lead-discovery-dashboard">
      {/* Hero Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9A — Lead Generation & Discovery</p>
          <h2>Lead Discovery Dashboard</h2>
          <div className="hero-stats">
            <span>{metrics?.total_leads || 0} Total Leads</span>
            <span>{metrics?.hot_leads || 0} Hot</span>
            <span>{metrics?.warm_leads || 0} Warm</span>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            className="primary-btn"
            onClick={() => onNavigate('import')}
            data-testid="discovery-import-btn"
          >
            <Plus size={16} />
            Import Leads
          </Button>
          <Button
            className="ghost-btn"
            onClick={() => onNavigate('database')}
            data-testid="discovery-database-btn"
          >
            <Database size={16} />
            Full Database
          </Button>
        </div>
      </section>

      {/* Metrics KPI Grid */}
      <section className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <MetricCard
          label="Total Leads"
          value={metrics?.total_leads || 0}
          icon={Database}
          loading={metricsLoading}
        />
        <MetricCard
          label="Hot Leads"
          value={metrics?.hot_leads || 0}
          icon={TrendingUp}
          loading={metricsLoading}
        />
        <MetricCard
          label="Warm Leads"
          value={metrics?.warm_leads || 0}
          icon={Target}
          loading={metricsLoading}
        />
        <MetricCard
          label="Pipeline Value"
          value={`$${Math.round((metrics?.pipeline_value || 0) / 1000)}K`}
          icon={TrendingUp}
          loading={metricsLoading}
        />
        <MetricCard
          label="Est. Revenue"
          value={`$${Math.round((metrics?.estimated_revenue_opportunity || 0) / 1000)}K`}
          icon={Sparkles}
          loading={metricsLoading}
        />
      </section>

      {/* Search & Filters */}
      <section className="glass-panel">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-white/40" />
            <Input
              placeholder="Search leads by name, email, city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearch}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="lead-search-input"
            />
          </div>
          <Button
            className="ghost-btn"
            onClick={fetchLeads}
            data-testid="search-execute-btn"
          >
            <RefreshCw size={16} />
            Search
          </Button>
        </div>

        {/* Quick Filters */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div>
            <label className="text-xs text-white/60 mb-2 block">City</label>
            <Input
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              placeholder="Any city"
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-white/60 mb-2 block">State</label>
            <Input
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              placeholder="Any state"
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-white/60 mb-2 block">Category</label>
            <Input
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              placeholder="All categories"
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-white/60 mb-2 block">Score</label>
            <Input
              value={filters.score_label}
              onChange={(e) => setFilters({ ...filters, score_label: e.target.value })}
              placeholder="All scores"
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
        </div>
      </section>

      {/* Leads Grid */}
      {loading ? (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="glass-panel">
              <Skeleton className="h-6 w-3/4 mb-2 bg-white/10" />
              <Skeleton className="h-4 w-1/2 mb-4 bg-white/10" />
              <Skeleton className="h-4 w-full mb-2 bg-white/10" />
              <Skeleton className="h-4 w-full bg-white/10" />
            </div>
          ))}
        </section>
      ) : leads.length === 0 ? (
        <section className="glass-panel">
          <div className="text-center py-16">
            <Search className="h-16 w-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white/90 mb-2">No leads found</h3>
            <p className="text-white/60 mb-6">Create your first lead or import from CSV</p>
            <div className="flex gap-4 justify-center">
              <Button
                className="primary-btn"
                onClick={() => onNavigate('import')}
                data-testid="empty-state-import-btn"
              >
                <Plus size={16} />
                Import CSV
              </Button>
            </div>
          </div>
        </section>
      ) : (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
        </section>
      )}
    </div>
  );
};

export default LeadDiscoveryDashboard;
